<template>
  <intro-container>
    <div
      :class="[
        'signup__container',
        'd-flex',
        'justify-center',
        'fill-height',
        signupDone ? 'align-top' : 'align-center',
      ]"
    >
      <v-snackbar
        :timeout="0"
        v-model="snackbar"
        color="error"
        top
        absolute
        centered
      >
        <p>{{ errorMessage }}</p>
        <v-icon class="snackbar-icon" @click="snackbar = false">
          $iconClose
        </v-icon>
      </v-snackbar>
      <div class="signup__done pt-12 mt-2" v-if="signupDone">
        <div class="signup__confirm-form" v-if="!userConfirmed">
          <v-form
            ref="confirmForm"
            v-model="valid"
            @submit.prevent="startConfirmSignup"
            lazy-validation
          >
            <h1 class="mb-6">{{ $t("signup.form.confirm.title") }}</h1>
            <p class="body1 mt-4 mb-2">
              {{
                $t("signup.form.confirm.description", {
                  email: enteredEmail || email,
                })
              }}
            </p>
            <div class="d-flex justify-end">
              <div>
                <niko-text-button
                  :text="$t('signup.form.confirm.resend')"
                  color="primary"
                  class="mx-1"
                  @onClick="resendEmail"
                />
              </div>
            </div>
            <v-subheader class="mb-2 mt-4 ml-0 pl-0" style="height: auto">
              {{ $t("signup.form.confirm.label") }}
            </v-subheader>
            <v-text-field
              v-model="code"
              :placeholder="$t('signup.form.confirm.label')"
              :rules="[
                (v) => !!v || $t('login.form.error.required'),
                inlineCodeMismatchError
                  ? $t('signup.form.confirm.error')
                  : true,
              ]"
              @click="inlineCodeMismatchError = false"
              hide-details="auto"
              required
              rounded
              outlined
              filled
            />
            <niko-button
              :text="$t('signup.form.confirm.button')"
              width="100%"
              class="mt-8"
              type="submit"
              primary
            />
          </v-form>
        </div>
        <div class="signup__confirmed" v-else>
          <h1 class="mb-6">
            {{ $t("signup.form.confirm.success.title") }}
          </h1>
          <p class="body1 mt-4">
            {{ $t("signup.form.confirm.success.description") }}
          </p>
          <niko-button
            :text="$t('login.form.button')"
            width="100%"
            class="mt-10"
            type="button"
            to="/login"
            primary
          />
        </div>
      </div>
      <div class="signup__form pa-3" v-else>
        <h1 class="mb-6">{{ $t("signup.form.title") }}</h1>
        <v-form
          ref="signupForm"
          v-model="valid"
          @submit.prevent="startSignup"
          lazy-validation
        >
          <v-subheader class="mb-2 ml-0 pl-0" style="height: auto">
            {{ $t("signup.form.firstname") }}
          </v-subheader>
          <v-text-field
            v-model="firstname"
            :placeholder="$t('signup.form.firstname')"
            :rules="[(v) => !!v || $t('login.form.error.required')]"
            hide-details="auto"
            required
            rounded
            outlined
            filled
          />
          <v-subheader class="mb-2 mt-4 ml-0 pl-0" style="height: auto">
            {{ $t("signup.form.lastname") }}
          </v-subheader>
          <v-text-field
            v-model="lastname"
            :placeholder="$t('signup.form.lastname')"
            :rules="[(v) => !!v || $t('login.form.error.required')]"
            hide-details="auto"
            required
            rounded
            outlined
            filled
          />
          <v-subheader class="mb-2 mt-4 ml-0 pl-0" style="height: auto">
            {{ $t("signup.form.email") }}
          </v-subheader>
          <v-text-field
            v-model="enteredEmail"
            :placeholder="$t('signup.form.email')"
            :rules="[(v) => !!v || $t('login.form.error.required')]"
            hide-details="auto"
            required
            rounded
            outlined
            filled
          />
          <v-subheader class="mb-2 mt-4 ml-0 pl-0" style="height: auto">
            {{ $t("signup.form.password") }}
          </v-subheader>
          <v-text-field
            v-model="password"
            :placeholder="$t('signup.form.password')"
            :rules="[(v) => !!v || $t('login.form.error.required')]"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? '$iconEyeHide' : '$iconEyeShow'"
            @click:append="showPassword = !showPassword"
            @focus="isPasswordFieldActive = true"
            @blur="isPasswordFieldActive = false"
            hide-details="auto"
            required
            rounded
            outlined
            filled
          />
          <niko-password-error
            :password="password"
            :isActive="isPasswordFieldActive"
            @isError="(value) => (passwordError = value)"
          />
          <v-checkbox
            v-model="disclaimer"
            class="mb-6 mt-4"
            :rules="[(v) => !!v || $t('login.form.error.required')]"
            hide-details
          >
            <template v-slot:label>
              <div class="disclaimer-label">
                {{ $t("signup.form.disclaimer.accept") }}
                <a
                  target="_blank"
                  href="#/disclaimer"
                  class="disclaimer-label__link"
                  @click.stop
                >
                  {{ $t("signup.form.disclaimer.link") }}
                </a>
              </div>
            </template>
          </v-checkbox>
          <vue-recaptcha
            :sitekey="recaptchaKey"
            @verify="captchaVerify"
            @expired="captchaError"
            @error="captchaError"
            :loadRecaptchaScript="true"
            theme="dark"
            :class="[
              'recaptcha',
              this.showRecaptchaError && 'recaptcha--error',
            ]"
          />
          <niko-button
            :text="$t('signup.form.button')"
            width="100%"
            class="mt-8"
            type="submit"
            primary
          />
          <niko-button
            :text="$t('signup.form.login_direct_button')"
            width="100%"
            class="mt-8"
            type="button"
            to="/login"
            secondary
          />
        </v-form>
      </div>
    </div>
  </intro-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import VueRecaptcha from "vue-recaptcha";
import NikoButton from "@/components/UI/NikoButton";
import NikoTextButton from "@/components/UI/NikoTextButton";
import NikoPasswordError from "@/components/UI/NikoPasswordError";
import IntroContainer from "@/components/IntroContainer";
import { emailRegex, passwordRegex, recaptchaKey } from "@/utils/constants";

export default {
  name: "signup",
  components: {
    NikoButton,
    NikoTextButton,
    NikoPasswordError,
    VueRecaptcha,
    IntroContainer,
  },
  data: () => ({
    emailRegex: emailRegex,
    passwordRegex: passwordRegex,
    recaptchaKey: recaptchaKey,
    valid: true,
    snackbar: false,
    errorMessage: "",
    showPassword: false,
    recaptchaVerified: false,
    showRecaptchaError: false,
    disclaimer: false,
    isPasswordFieldActive: false,
    passwordError: false,
    firstname: "",
    lastname: "",
    enteredEmail: "",
    password: "",
    code: "",
    inlineCodeMismatchError: false,
  }),
  computed: {
    ...mapState("authStore", ["error", "signupDone", "userConfirmed", "email"]),
  },
  methods: {
    ...mapActions("authStore", [
      "signup",
      "resendConfirmationCode",
      "confirmSignup",
    ]),
    ...mapMutations("authStore", ["setError"]),
    startSignup() {
      if (this.validate()) {
        this.signup({
          firstname: this.firstname,
          lastname: this.lastname,
          email: this.enteredEmail.toLowerCase(),
          password: this.password,
        });
      }
    },
    validate() {
      let isFormValid = this.$refs.signupForm.validate() && !this.passwordError;
      if (!this.recaptchaVerified) {
        this.showRecaptchaError = true;
        isFormValid = false;
      }
      if (!isFormValid) {
        this.snackbar = true;
        this.errorMessage = this.passwordError
          ? this.$i18n.t("signup.error.password_requirements")
          : this.$i18n.t("signup.error.fields");
      } else {
        this.snackbar = false;
        this.errorMessage = "";
      }
      return isFormValid;
    },
    captchaVerify() {
      this.recaptchaVerified = true;
      this.showRecaptchaError = false;
    },
    captchaError() {
      this.recaptchaVerified = false;
    },
    resendEmail() {
      this.resendConfirmationCode(
        this.enteredEmail.toLowerCase() || this.email.toLowerCase()
      );
    },
    startConfirmSignup() {
      this.setError(null);
      if (this.$refs.confirmForm.validate()) {
        this.confirmSignup({
          username: this.enteredEmail.toLowerCase() || this.email.toLowerCase(),
          code: this.code,
        });
      }
    },
  },
  watch: {
    error(value) {
      this.snackbar = !!value;
      if (value) {
        const errorCode = value.code;
        if (this.$i18n.te(`signup.error.${errorCode}`)) {
          this.errorMessage = this.$i18n.t(`signup.error.${errorCode}`);
          if (errorCode === "CodeMismatchException") {
            this.inlineCodeMismatchError = true;
          }
        } else {
          this.errorMessage = this.$i18n.t("signup.error.default");
        }
      }
    },
  },
};
</script>

<style lang="scss">
.signup {
  overflow: auto;

  &__row {
    min-height: 500px;
  }
  &__container {
    position: relative;
  }

  &__form,
  &__done {
    flex-basis: 304px;
  }
}

.v-text-field--enclosed .v-input__append-inner {
  margin-top: 8px;
}

.v-text-field__details {
  padding-left: 0 !important;
}

.v-input--checkbox {
  position: relative;
}

.v-input.error--text:before,
.recaptcha--error:before {
  content: "";
  height: calc(100% + 20px);
  width: 4px;
  background: var(--v-error-base);
  position: absolute;
  left: -12px;
  top: -24px;
}

.v-input--hide-details.error--text:before {
  height: 70px;
}

.v-input--checkbox.error--text:before {
  height: 100%;
  top: 0;
}

.recaptcha--error:before {
  top: -10px;
  height: 98px;
}

.recaptcha {
  display: flex;
  justify-content: center;
  position: relative;
}

.snackbar-icon {
  width: 16px !important;
  height: 16px;
}

.disclaimer-label {
  color: #fff;

  &__link {
    text-decoration: none;
  }
}
</style>
